import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Car, CheckCircle2, Upload } from "lucide-react";

export default function Onboarding() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    make: "", model: "", year: "", plate: "", mileage: "", color: "", fuel_type: "Petrol",
  });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async () => {
    setLoading(true);
    try {
      await api.post("/vehicles", {
        ...form,
        year: parseInt(form.year) || new Date().getFullYear(),
        mileage: parseInt(form.mileage) || 0,
      });
      await refresh();
      toast.success("Your car is set up! Welcome to VecPadi.");
      navigate("/app");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const canNext = form.make && form.model && form.year;

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto flex max-w-lg flex-col px-5 py-8">
        <Logo />
        <div className="mt-8 flex gap-2">
          {[1, 2, 3].map((s) => (
            <div key={s} className={`h-1.5 flex-1 rounded-full transition-colors ${s <= step ? "bg-emerald-500" : "bg-zinc-200"}`} />
          ))}
        </div>

        {step === 1 && (
          <div className="mt-8 vp-fade-up">
            <div className="grid h-12 w-12 place-items-center rounded-2xl bg-emerald-50 text-emerald-600"><Car size={24} /></div>
            <h1 className="mt-5 font-display text-2xl font-bold tracking-tight">Tell us about your car</h1>
            <p className="mt-1 text-sm text-muted-foreground">This helps VecPadi look after it properly.</p>
            <div className="mt-6 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Make</Label><Input data-testid="ob-make" value={form.make} onChange={set("make")} placeholder="Toyota" className="mt-1.5 h-12 rounded-xl" /></div>
                <div><Label>Model</Label><Input data-testid="ob-model" value={form.model} onChange={set("model")} placeholder="Corolla" className="mt-1.5 h-12 rounded-xl" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Year</Label><Input data-testid="ob-year" type="number" value={form.year} onChange={set("year")} placeholder="2015" className="mt-1.5 h-12 rounded-xl" /></div>
                <div><Label>Color</Label><Input data-testid="ob-color" value={form.color} onChange={set("color")} placeholder="Silver" className="mt-1.5 h-12 rounded-xl" /></div>
              </div>
            </div>
            <Button data-testid="ob-next-1" disabled={!canNext} onClick={() => setStep(2)} className="mt-8 w-full h-12 rounded-xl bg-emerald-500 hover:bg-emerald-600">Continue</Button>
          </div>
        )}

        {step === 2 && (
          <div className="mt-8 vp-fade-up">
            <h1 className="font-display text-2xl font-bold tracking-tight">Plate & mileage</h1>
            <p className="mt-1 text-sm text-muted-foreground">So we can track service and documents.</p>
            <div className="mt-6 space-y-4">
              <div><Label>Plate number</Label><Input data-testid="ob-plate" value={form.plate} onChange={set("plate")} placeholder="ABC-123-XY" className="mt-1.5 h-12 rounded-xl" /></div>
              <div><Label>Current mileage (km)</Label><Input data-testid="ob-mileage" type="number" value={form.mileage} onChange={set("mileage")} placeholder="85000" className="mt-1.5 h-12 rounded-xl" /></div>
              <div>
                <Label>Fuel type</Label>
                <div className="mt-1.5 flex gap-2">
                  {["Petrol", "Diesel", "Hybrid", "Electric"].map((f) => (
                    <button key={f} type="button" onClick={() => setForm({ ...form, fuel_type: f })}
                      className={`flex-1 rounded-xl border px-2 py-2.5 text-sm font-medium transition-colors ${form.fuel_type === f ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-zinc-200 text-zinc-600"}`}>
                      {f}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="mt-8 flex gap-3">
              <Button variant="outline" onClick={() => setStep(1)} className="h-12 rounded-xl px-6">Back</Button>
              <Button data-testid="ob-next-2" onClick={() => setStep(3)} className="flex-1 h-12 rounded-xl bg-emerald-500 hover:bg-emerald-600">Continue</Button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="mt-8 vp-fade-up">
            <h1 className="font-display text-2xl font-bold tracking-tight">Almost done!</h1>
            <p className="mt-1 text-sm text-muted-foreground">You can upload documents later from Car Care.</p>
            <div className="mt-6 rounded-2xl border border-dashed border-zinc-300 bg-white p-6 text-center">
              <Upload className="mx-auto text-zinc-400" size={28} />
              <p className="mt-2 text-sm font-medium">Vehicle documents (optional)</p>
              <p className="text-xs text-zinc-400">Licence, insurance, roadworthiness</p>
              <p className="mt-3 text-xs text-emerald-600">You can add these anytime — no wahala.</p>
            </div>
            <div className="mt-6 rounded-2xl bg-emerald-50 p-5">
              <div className="flex items-center gap-2 text-emerald-700"><CheckCircle2 size={18} /><span className="font-semibold text-sm">Your {form.make} {form.model}</span></div>
              <p className="mt-1 text-sm text-emerald-600">{form.year} • {form.plate || "No plate yet"} • {form.mileage || 0} km</p>
            </div>
            <div className="mt-8 flex gap-3">
              <Button variant="outline" onClick={() => setStep(2)} className="h-12 rounded-xl px-6">Back</Button>
              <Button data-testid="ob-finish" onClick={submit} disabled={loading} className="flex-1 h-12 rounded-xl bg-emerald-500 hover:bg-emerald-600">
                {loading ? <Loader2 className="animate-spin" size={18} /> : "Finish setup"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
